import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [messageLength, setMessageLength] = useState(0);
  const [consents, setConsents] = useState({
    smsConsent: false,
    infoConsent: false,
    marketingConsent: false,
    noSms: false,
  });

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://metamorphobiz.com';
    const schemaId = 'schema-contact';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact Us | MetamorphoBiz Inc.',
        description: 'Contact MetamorphoBiz Inc. in Houston, TX for government AI data analytics, decision systems, and operational optimization inquiries.',
        url: `${siteUrl}/contact`,
        inLanguage: 'en-US',
        isPartOf: { '@type': 'WebSite', url: siteUrl },
        publisher: {
          '@type': 'Organization',
          name: 'MetamorphoBiz Inc.',
          url: siteUrl,
          telephone: '+1-844-667-7433',
          email: 'contact@metamorphobiz.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '340 N Sam Houston Pkwy E Ste A165G',
            addressLocality: 'Houston',
            addressRegion: 'TX',
            postalCode: '77060',
            addressCountry: 'US',
          },
        },
      });
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleConsentChange = (key: keyof typeof consents) => {
    setConsents(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (messageLength > 500) return;

    setFormStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Build consent string
    const consentValues: string[] = [];
    if (consents.smsConsent) consentValues.push('Consent to SMS communications from +18446677433');
    if (consents.infoConsent) consentValues.push('Consent to receive informational messages');
    if (consents.marketingConsent) consentValues.push('Consent to receive marketing text messages');
    if (consents.noSms) consentValues.push('Does not want to receive text messages');
    formData.set('sms_consent_options', consentValues.join(' | '));

    try {
      const response = await fetch('https://readdy.ai/api/form/d6n45b46fojmejgajlgg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setMessageLength(0);
        setConsents({ smsConsent: false, infoConsent: false, marketingConsent: false, noSms: false });
        setTimeout(() => setFormStatus('idle'), 6000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const consentItems = [
    {
      key: 'smsConsent' as const,
      label: 'Metamorphobiz would like your consent to send text message communications from +18446677433 to your mobile number listed above, regarding informational, marketing. Consent is not a condition of purchase. Message frequency varies. Message and data rates may apply. Reply \'STOP\' to unsubscribe at any time. Reply \'HELP\' for assistance or more information.',
    },
    {
      key: 'infoConsent' as const,
      label: 'Yes, I consent to receive informational messages from Metamorphobiz regarding informational, marketing.',
    },
    {
      key: 'marketingConsent' as const,
      label: 'Yes, I consent to receive marketing text messages from Metamorphobiz.',
    },
    {
      key: 'noSms' as const,
      label: 'No, I do not want to receive any text messages from Metamorphobiz.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white shadow-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/8cd6047605fa8bd9b83f93f5c3fc7ad9/47c456da8a70802ef8f75066d63b6e27.png"
                alt="MetamorphoBiz Logo"
                className="h-12 w-auto"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowrap">Home</Link>
              <Link to="/contact" className="text-sm font-medium text-teal-600 whitespace-nowrap">Contact Us</Link>
              <Link to="/privacy" className="text-sm font-medium text-slate-700 hover:text-teal-600 transition-colors whitespace-nowrap">Privacy Policy</Link>
            </div>
            <Link
              to="/"
              className="px-6 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-all hover:shadow-lg cursor-pointer whitespace-nowrap"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center space-y-4">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900">Contact Us</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Have a question or want to learn more about our services? Fill out the form below and we'll get back to you shortly.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12">

            {/* Left: Contact Info */}
            <div className="md:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Get In Touch</h2>
                <p className="text-base text-slate-600 leading-relaxed">
                  We're here to help. Reach out to us through any of the channels below or use the form to send us a message directly.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">Address</h3>
                    <p className="text-sm text-slate-600">
                      340 N Sam Houston Pkwy E Ste A165G<br />
                      Houston, TX 77060 USA
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">Phone</h3>
                    <a href="tel:18446677433" className="text-sm text-teal-600 hover:text-teal-700">1-844-667-7433</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">Email</h3>
                    <a href="mailto:contact@metamorphobiz.com" className="text-sm text-teal-600 hover:text-teal-700">
                      contact@metamorphobiz.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500 leading-relaxed">
                  We do not share your mobile opt-in information with anyone. See our{' '}
                  <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link>{' '}
                  for more information on how we handle your data.
                </p>
              </div>
            </div>

            {/* Right: Form */}
            <div className="md:col-span-3">
              <div className="bg-slate-50 rounded-3xl p-8 shadow-sm">
                <form
                  id="contact-us-page-form"
                  data-readdy-form
                  className="space-y-6"
                  onSubmit={handleSubmit}
                >
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm bg-white"
                      placeholder="Your full name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm bg-white"
                      placeholder="your@email.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all text-sm bg-white"
                      placeholder="(555) 000-0000"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      onChange={e => setMessageLength(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-teal-600 focus:ring-2 focus:ring-teal-600/20 outline-none transition-all resize-none text-sm bg-white"
                      placeholder="Tell us how we can help..."
                    ></textarea>
                    <div className="flex justify-between mt-1">
                      <p className="text-xs text-slate-500">Maximum 500 characters</p>
                      <p className={`text-xs ${messageLength > 500 ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>
                        {messageLength}/500
                      </p>
                    </div>
                  </div>

                  {/* SMS Consent Checkboxes */}
                  <div className="space-y-4 pt-2">
                    <p className="text-sm font-semibold text-slate-900">SMS &amp; Communication Preferences</p>
                    {consentItems.map(item => (
                      <label
                        key={item.key}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <div className="relative flex-shrink-0 mt-0.5">
                          <input
                            type="checkbox"
                            name={item.key}
                            checked={consents[item.key]}
                            onChange={() => handleConsentChange(item.key)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            consents[item.key]
                              ? 'bg-teal-600 border-teal-600'
                              : 'border-slate-400 bg-white group-hover:border-teal-500'
                          }`}>
                            {consents[item.key] && (
                              <i className="ri-check-line text-white text-xs"></i>
                            )}
                          </div>
                        </div>
                        <span className="text-sm text-slate-700 leading-relaxed">{item.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Privacy note */}
                  <p className="text-xs text-slate-500 leading-relaxed border-t border-slate-200 pt-4">
                    We do not share your mobile opt-in information with anyone. See our{' '}
                    <Link to="/privacy" className="text-teal-600 hover:underline">Privacy Policy</Link>{' '}
                    for more information on how we handle your data.
                  </p>

                  {/* Status messages */}
                  {formStatus === 'success' && (
                    <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg text-teal-800 text-sm flex items-center gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 text-lg"></i>
                      Thank you! Your message has been sent successfully. We'll be in touch soon.
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm flex items-center gap-2">
                      <i className="ri-error-warning-fill text-red-500 text-lg"></i>
                      Something went wrong. Please try again or email us directly.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting' || messageLength > 500}
                    className="w-full px-6 py-4 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-all hover:shadow-lg cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    {formStatus === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </span>
                    ) : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-400">© 2024 MetamorphoBiz. All rights reserved.</p>
            <div className="flex gap-8">
              <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Home</Link>
              <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Contact Us</Link>
              <Link to="/privacy" className="text-sm text-slate-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
